// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import { push } from "connected-react-router";
import type { Action } from "redux-action";

import http from "@http";
import { route } from "@routes/helper";
import { isFirstCareUser } from "@modules/account/selectors";
import actionCreators from "@modules/service/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import { transformError } from "@transformers/response";
import {
  GetAbsenceMonitoringList,
  GetAbsenceMonitoring,
  GetAbsenceMonitoringSchema,
  AddAbsenceMonitoring,
  StartDraftAbsenceMonitoring,
  DuplicateAbsenceMonitoring,
  UpdateAbsenceMonitoring,
  UpdateAbsenceMonitoringAssignments,
  DisableAbsenceMonitoring,
  DiscardAbsenceMonitoringDraft,
  SubmitAbsenceMonitoringForReview,
  CancelAbsenceMonitoringReview,
  CancelAbsenceMonitoringReviewAndDiscard,
} from "@modules/service/constants/actions";
import { delay } from "@sagas/utils";

import { ApprovalStateEnum } from "@modules/service/constants";
import type {
  ApprovableContainer,
  HierarchyAssignment,
  HierarchyAssignmentSummary,
} from "@modules/service/types";
import type { AbsenceMonitoring } from "@modules/service/reducer/absence-monitoring/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { parseSchema } from "@utils/schema";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type ItemResponse<T> = $Exact<T>;

// READ

type AbsenceMonitoringListRequest = $Exact<{
  clientId: string,
}>;

export function* onGetAbsenceMonitoringList({
  payload,
}: Action<AbsenceMonitoringListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<ApprovableContainer<AbsenceMonitoring>>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process`,
    );

    yield put(actionCreators.GetAbsenceMonitoringListSuccess(response.data.content));
  } catch (e) {
    yield put(actionCreators.GetAbsenceMonitoringListError(transformError(e)));
  }
}

type AbsenceMonitoringRequest = AbsenceMonitoringListRequest &
  $Exact<{
    processId: string,
  }>;

export function* onGetAbsenceMonitoring({
  payload,
}: Action<AbsenceMonitoringRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<AbsenceMonitoring>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error("Invalid payload provided");
    }

    const stableResponse = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${payload.processId}`,
    );
    const assignmentsResponse = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${payload.processId}/assignment`,
    );

    yield all([
      put(actionCreators.GetAbsenceMonitoringSuccess(stableResponse.data)),
      put(actionCreators.SetAbsenceMonitoringAssignments(assignmentsResponse.data)),
    ]);
  } catch (e) {
    yield put(actionCreators.GetAbsenceMonitoringError(transformError(e)));
  }
}

// REFRESH

type RefreshRequest = $Exact<{
  clientId: string,
  processId?: string,
}>;

export function* onRefresh(params: RefreshRequest): Generator<*, void, Object> {
  yield put(actionCreators.GetAbsenceMonitoringList({ clientId: params.clientId }));

  if (params.processId) {
    yield put(
      actionCreators.GetAbsenceMonitoring({
        clientId: params.clientId,
        processId: params.processId,
      }),
    );
  }
}

// CREATE

export function* onGetAbsenceMonitoringSchema({
  payload,
}: Action<AbsenceMonitoringListRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId) {
      throw new Error("Invalid payload provided");
    }
    const schema = yield parseSchema("AbsenceMonitoringProcessRevisionDTO", {
      clientId: payload.clientId,
    });

    yield put(actionCreators.GetAbsenceMonitoringSchemaSuccess({ schema }));
  } catch (e) {
    yield put(actionCreators.GetAbsenceMonitoringSchemaError(transformError(e)));
  }
}

export function* onStartDraftAbsenceMonitoring({
  payload,
}: Action<AbsenceMonitoringRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<AbsenceMonitoring>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${payload.processId}`,
    );

    yield all([
      put(actionCreators.StartDraftAbsenceMonitoringSuccess(response.data)),
      put(actionCreators.SetShowEditAbsenceMonitoringModal(true)),
    ]);
  } catch (e) {
    yield put(actionCreators.StartDraftAbsenceMonitoringError(transformError(e)));
  }
}

export function* onDuplicateAbsenceMonitoring({
  payload,
}: Action<AbsenceMonitoringRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<AbsenceMonitoring>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${payload.processId}/active`,
    );

    yield all([
      put(actionCreators.DuplicateAbsenceMonitoringSuccess(response.data)),
      put(actionCreators.SetAbsenceMonitoringAssignments([])),
      put(actionCreators.SetShowAddAbsenceMonitoringModal(true)),
      put(push(route("ADMINISTRATION_SERVICE_PROCESSES", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.DuplicateAbsenceMonitoringError(transformError(e)));
  }
}

type AbsenceMonitoringAddRequest = AbsenceMonitoringListRequest &
  $Exact<{
    draft: AbsenceMonitoring,
    assignments: Array<HierarchyAssignment>,
  }>;

export function* onAddAbsenceMonitoring({
  payload,
}: Action<AbsenceMonitoringAddRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.draft || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process`,
      payload.draft,
    );

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${response.data.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    const firstCareUser = yield select(isFirstCareUser);
    if (firstCareUser) {
      yield call(
        http.post,
        `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${response.data.id}/submit-for-review`,
        {
          notes: "Automatically submitted",
        },
      );
    }
    yield call(onRefresh, { clientId: payload.clientId });

    yield all([
      sendSuccessMessage("DRAFT_CREATED"),
      put(actionCreators.AddAbsenceMonitoringSuccess(response.data)),
      put(actionCreators.SetShowAddAbsenceMonitoringModal(false)),
    ]);
  } catch (e) {
    yield put(actionCreators.AddAbsenceMonitoringError(transformError(e)));
  }
}

// UPDATE

type AbsenceMonitoringUpdateRequest = AbsenceMonitoringRequest &
  $Exact<{
    draft: AbsenceMonitoring,
  }>;

export function* onUpdateAbsenceMonitoring({
  payload,
}: Action<AbsenceMonitoringUpdateRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.processId || !payload.draft) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${payload.processId}/draft`,
      payload.draft,
    );

    const firstCareUser = yield select(isFirstCareUser);
    if (firstCareUser) {
      yield call(
        http.post,
        `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${payload.processId}/submit-for-review`,
        {
          notes: "Automatically submitted",
        },
      );
    }

    yield all([
      put(actionCreators.UpdateAbsenceMonitoringSuccess(response.data)),
      put(actionCreators.SetShowEditAbsenceMonitoringModal(false)),
    ]);

    yield call(onRefresh, { clientId: payload.clientId, processId: payload.processId });
    yield put(sendSuccessMessage("DRAFT_UPDATED"));
  } catch (e) {
    yield put(actionCreators.UpdateAbsenceMonitoringError(transformError(e)));
  }
}

type AbsenceMonitoringUpdateAssignmentsRequest = $Exact<{
  container: ApprovableContainer<AbsenceMonitoring>,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onUpdateAbsenceMonitoringAssignments({
  payload,
}: Action<AbsenceMonitoringUpdateAssignmentsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.container || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const prevAssignmentsResponse = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/absence-monitoring/process/${payload.container.id}/assignment`,
    );
    if (prevAssignmentsResponse.data && prevAssignmentsResponse.data.length) {
      yield all([
        ...prevAssignmentsResponse.data.map((a: HierarchyAssignmentSummary) => {
          return call(
            http.delete,
            `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/absence-monitoring/process/${payload.container.id}/assignment/${a.id}`,
          );
        }),
      ]);
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/absence-monitoring/process/${payload.container.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield call(onRefresh, {
      clientId: payload.container.clientId,
      processId: payload.container.id,
    });

    yield put(actionCreators.SetShowEditAbsenceMonitoringAssignmentsModal(false));
  } catch (e) {
    yield put(actionCreators.UpdateAbsenceMonitoringAssignmentsError(transformError(e)));
  }
}

// DISABLE

export type AbsenceMonitoringDisableRequest = AbsenceMonitoringRequest &
  $Exact<{
    approvable: ApprovableContainer<AbsenceMonitoring>,
  }>;

export function* onDisable({
  payload,
}: Action<AbsenceMonitoringDisableRequest>): Generator<
  *,
  void,
  ItemResponse<ApprovableContainer<AbsenceMonitoring>>,
> {
  try {
    if (!payload || !payload.clientId || !payload.processId || !payload.approvable) {
      throw Error("Invalid payload provided");
    }

    const draft =
      payload.approvable.approvalState === ApprovalStateEnum.ACTIVE_ONLY
        ? payload.approvable.active
        : payload.approvable.draft;

    if (!draft) {
      throw Error("Invalid draft");
    }

    yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${payload.processId}/draft`,
      {
        ...draft,
        activeFrom: draft.activeTo ? new Date().toISOString() : draft.activeFrom,
        activeTo: draft.activeTo ? null : new Date().toISOString(),
      },
    );

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${payload.processId}/submit-for-review`,
      {
        notes: "AbsenceMonitoring disabled and resubmitted for review",
      },
    );

    yield all([
      call(delay, 1000),
      put(actionCreators.SetShowDisableAbsenceMonitoringModal(false)),
      put(actionCreators.GetAbsenceMonitoringList({ clientId: payload.clientId })),
      put(sharedActionCreators.GetAlerts({ clientId: payload.clientId })),
      put(sharedActionCreators.GetServiceSectionStatuses({ clientId: payload.clientId })),
      put(sharedActionCreators.GetHierarchyTree({ clientId: payload.clientId })),
      put(push(route("ADMINISTRATION_SERVICE_PROCESSES", { clientId: payload.clientId }))),
    ]);
    yield put(sendSuccessMessage("DRAFT_UPDATED"));
  } catch (e) {
    yield put(actionCreators.DisableAbsenceMonitoringError(transformError(e)));
  }
}

// STATE TRANSITIONS

export type AbsenceMonitoringTransitionRequest = $Exact<{
  clientId: string,
  processId: string,
}>;

export function* onDiscardDraft({
  payload,
}: Action<AbsenceMonitoringTransitionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${payload.processId}/draft`,
    );

    yield all([
      put(actionCreators.DiscardAbsenceMonitoringDraftSuccess()),
      put(actionCreators.SetShowDiscardAbsenceMonitoringModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_PROCESSES", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.DiscardAbsenceMonitoringDraftError(transformError(e)));
  }
}

export type AbsenceMonitoringSubmitForReviewRequest = $Exact<{
  ...AbsenceMonitoringTransitionRequest,
  notes: string,
}>;

export function* onSubmitForReview({
  payload,
}: Action<AbsenceMonitoringSubmitForReviewRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.processId || !payload.notes) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${payload.processId}/submit-for-review`,
      {
        notes: payload.notes,
      },
    );

    yield all([
      put(actionCreators.SubmitAbsenceMonitoringForReviewSuccess()),
      put(actionCreators.SetShowSubmitAbsenceMonitoringModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_PROCESSES", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.SubmitAbsenceMonitoringForReviewError(transformError(e)));
  }
}

export function* onCancelReview({
  payload,
}: Action<AbsenceMonitoringTransitionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${payload.processId}/cancel-review`,
    );

    yield all([
      put(actionCreators.CancelAbsenceMonitoringReviewSuccess()),
      put(actionCreators.SetShowCancelAbsenceMonitoringModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_PROCESSES", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelAbsenceMonitoringReviewError(transformError(e)));
  }
}

export function* onCancelReviewAndDiscard({
  payload,
}: Action<AbsenceMonitoringTransitionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${payload.processId}/cancel-review`,
    );
    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${payload.processId}/draft`,
    );

    yield all([
      put(actionCreators.CancelAbsenceMonitoringReviewSuccess()),
      put(actionCreators.SetShowCancelDiscardAbsenceMonitoringModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_PROCESSES", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelAbsenceMonitoringReviewError(transformError(e)));
  }
}

// ROOT

export function* AbsenceMonitoringSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetAbsenceMonitoringList, onGetAbsenceMonitoringList),
    takeEvery(GetAbsenceMonitoring, onGetAbsenceMonitoring),
    takeEvery(GetAbsenceMonitoringSchema, onGetAbsenceMonitoringSchema),
    takeEvery(AddAbsenceMonitoring, onAddAbsenceMonitoring),
    takeEvery(StartDraftAbsenceMonitoring, onStartDraftAbsenceMonitoring),
    takeEvery(DuplicateAbsenceMonitoring, onDuplicateAbsenceMonitoring),
    takeEvery(UpdateAbsenceMonitoring, onUpdateAbsenceMonitoring),
    takeEvery(UpdateAbsenceMonitoringAssignments, onUpdateAbsenceMonitoringAssignments),
    takeEvery(DisableAbsenceMonitoring, onDisable),
    takeEvery(DiscardAbsenceMonitoringDraft, onDiscardDraft),
    takeEvery(SubmitAbsenceMonitoringForReview, onSubmitForReview),
    takeEvery(CancelAbsenceMonitoringReview, onCancelReview),
    takeEvery(CancelAbsenceMonitoringReviewAndDiscard, onCancelReviewAndDiscard),
  ]);
}

export default AbsenceMonitoringSagas;
